import React, { useState, useEffect } from 'react';
import { 
  Image as ImageIcon, Video, Search, RefreshCw, Check, Copy, 
  ExternalLink, FileQuestion, HardDrive, Play, Eye, 
  Sparkles, CheckCircle2, Film, Info
} from 'lucide-react';
import { MediaItem } from '../../types';

export const MediaLibrary: React.FC = () => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>('all');
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/media');
      if (res.ok) {
        const data = await res.json();
        setMediaItems(data.files || []);
      }
    } catch (e) {
      console.error('Грешка при четене на медийни файлове:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const showToast = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3500);
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    showToast(`Копиран адрес: ${url}`);
    setTimeout(() => setCopiedUrl(null), 2500);
  };

  const filteredItems = mediaItems.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) {
      return false;
    }
    if (search.trim()) {
      const query = search.toLowerCase();
      return (
        item.name.toLowerCase().includes(query) ||
        item.url.toLowerCase().includes(query) ||
        item.extension.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const imagesCount = mediaItems.filter(i => i.type === 'image').length;
  const videosCount = mediaItems.filter(i => i.type === 'video').length;

  return (
    <div className="space-y-6 font-sans">
      
      {/* Toast Notification */}
      {notification && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#0F1E32] text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 border border-[#C5A46E]/40 text-xs font-semibold animate-fade-in">
          <CheckCircle2 className="w-4 h-4 text-[#C5A46E]" />
          <span>{notification}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center shadow-md shrink-0">
              <HardDrive className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-base md:text-lg text-[#0F1E32]">
                  Медийна библиотека (папка public/)
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-100 text-emerald-800 border border-emerald-200">
                  Автоматично разпознаване
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
                Всички снимки и видеа, качени в папка <code className="bg-slate-100 px-1 py-0.5 rounded text-[#0F1E32] font-mono font-bold">public/</code> на проекта, се четат и визуализират автоматично след всеки deploy.
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              onClick={fetchMedia}
              disabled={loading}
              className="px-4 py-2.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-md"
              title="Пресканиране на папка public"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#C5A46E]' : ''}`} />
              <span>Презареди</span>
            </button>
          </div>
        </div>

        {/* GitHub Upload Instructions Banner */}
        <div className="mt-4 p-3.5 bg-amber-50/90 border border-amber-200/90 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 leading-relaxed shadow-xs">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <span>
            Нови снимки се добавят чрез качване в <code className="bg-amber-100/90 px-1.5 py-0.5 rounded font-mono font-bold text-amber-950">public/</code> папката на проекта (през GitHub).
            След автоматичния redeploy на Cloudflare те ще се появят тук автоматично.
          </span>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100">
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Общо намерени файлове</span>
            <span className="text-sm font-black text-[#0F1E32]">{mediaItems.length}</span>
          </div>
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5 text-[#C5A46E]" /> Снимки
            </span>
            <span className="text-sm font-black text-[#0F1E32]">{imagesCount}</span>
          </div>
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
              <Video className="w-3.5 h-3.5 text-sky-600" /> Видеоклипове
            </span>
            <span className="text-sm font-black text-[#0F1E32]">{videosCount}</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
        {/* Type Filter Buttons */}
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
              filterType === 'all' 
                ? 'bg-white text-[#0F1E32] shadow-sm' 
                : 'text-slate-600 hover:text-[#0F1E32]'
            }`}
          >
            Всички ({mediaItems.length})
          </button>
          <button
            onClick={() => setFilterType('image')}
            className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
              filterType === 'image' 
                ? 'bg-white text-[#0F1E32] shadow-sm' 
                : 'text-slate-600 hover:text-[#0F1E32]'
            }`}
          >
            <ImageIcon className="w-3.5 h-3.5 text-[#C5A46E]" />
            Снимки ({imagesCount})
          </button>
          <button
            onClick={() => setFilterType('video')}
            className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
              filterType === 'video' 
                ? 'bg-white text-[#0F1E32] shadow-sm' 
                : 'text-slate-600 hover:text-[#0F1E32]'
            }`}
          >
            <Video className="w-3.5 h-3.5 text-sky-600" />
            Видеа ({videosCount})
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-grow sm:flex-grow-0 sm:w-72">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Търсене по име на файл..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C5A46E] focus:bg-white"
          />
        </div>
      </div>

      {/* Grid of Media Items */}
      {loading && mediaItems.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
          <RefreshCw className="w-10 h-10 animate-spin text-[#C5A46E] mx-auto mb-3" />
          <h4 className="font-bold text-sm text-[#0F1E32]">Сканиране на файловете в public/</h4>
          <p className="text-xs text-slate-400 mt-1">Моля, изчакайте секунда...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <FileQuestion className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h4 className="font-bold text-sm text-[#0F1E32]">Няма намерени файлове в public/</h4>
          <p className="text-xs text-slate-400 max-w-md mx-auto mt-1 leading-relaxed">
            Поставете своите снимки (.jpg, .png, .webp) или видеа (.mp4, .webm) в папка <code>public/</code> и натиснете бутона „Презареди“.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredItems.map((item) => {
            const isCopied = copiedUrl === item.url;
            return (
              <div 
                key={item.id}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col group"
              >
                {/* Visual Preview */}
                <div className="relative aspect-video bg-slate-900 overflow-hidden flex items-center justify-center">
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 cursor-pointer"
                      onClick={() => setPreviewImage(item.url)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80';
                      }}
                    />
                  ) : (
                    <div 
                      onClick={() => setPreviewVideo(item.url)}
                      className="w-full h-full flex flex-col items-center justify-center text-white cursor-pointer relative group/video"
                    >
                      <video
                        src={item.url}
                        preload="metadata"
                        muted
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                      />
                      <div className="w-12 h-12 rounded-full bg-[#C5A46E] text-white flex items-center justify-center shadow-lg relative z-10 group-hover/video:scale-110 transition-transform">
                        <Play className="w-6 h-6 ml-0.5" />
                      </div>
                      <span className="relative z-10 text-[10px] uppercase font-bold tracking-wider text-slate-200 mt-2 bg-black/60 px-2 py-0.5 rounded">
                        Възпроизведи видео
                      </span>
                    </div>
                  )}

                  {/* Badges */}
                  <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase text-white shadow ${
                      item.type === 'image' ? 'bg-[#C5A46E]' : 'bg-sky-600'
                    }`}>
                      {item.type === 'image' ? 'Снимка' : 'Видео'}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-black/70 text-slate-200 font-bold backdrop-blur-xs">
                      .{item.extension}
                    </span>
                  </div>

                  <span className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded text-[9px] font-mono bg-black/70 text-slate-200 font-bold backdrop-blur-xs">
                    {item.size}
                  </span>
                </div>

                {/* Details */}
                <div className="p-4 flex flex-col flex-grow">
                  <h4 className="font-bold text-xs text-slate-800 break-all line-clamp-1" title={item.name}>
                    {item.name}
                  </h4>
                  <div className="mt-1 flex items-center justify-between text-[11px] font-mono text-[#C5A46E] font-bold">
                    <span>{item.url}</span>
                  </div>

                  <div className="mt-auto pt-3 border-t border-slate-100 flex items-center gap-2">
                    <button
                      onClick={() => handleCopy(item.url)}
                      className={`flex-grow py-2 px-3 rounded-lg text-[11px] font-bold uppercase tracking-wider transition flex items-center justify-center gap-1.5 cursor-pointer ${
                        isCopied
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 hover:bg-[#0F1E32] hover:text-white text-slate-700'
                      }`}
                      title="Копирай адреса за ползване в сайта"
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          Копиран!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-[#C5A46E]" />
                          Копирай URL
                        </>
                      )}
                    </button>

                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition"
                      title="Отвори файл в нов таб"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Guide Box */}
      <div className="bg-slate-50 border-l-4 border-[#C5A46E] rounded-r-2xl p-5 text-slate-700">
        <h4 className="font-bold text-sm text-[#0F1E32] flex items-center gap-2">
          <HardDrive className="w-4 h-4 text-[#C5A46E]" />
          Как работи папка public/ при нов deploy?
        </h4>
        <div className="text-xs text-slate-600 mt-2 space-y-1.5 leading-relaxed">
          <p>
            1. <strong>Поставяне на файлове:</strong> Когато добавите нови файлове в папка <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 font-mono text-[#0F1E32] font-bold">public/</code> (напр. <code>public/imot1.jpg</code> или <code>public/tour.mp4</code>) и направите нов deploy, те стават веднага активни на адрес <code className="font-mono text-[#C5A46E]">/imot1.jpg</code>.
          </p>
          <p>
            2. <strong>Автоматично разпознаване:</strong> Админ панелът автоматично сканира папката и веднага ги показва тук с техния размер, формат и предварителен преглед.
          </p>
          <p>
            3. <strong>Използване:</strong> При редактиране на имот, брокер или заглавен банер, просто натиснете бутона <strong>„📁 Избери от public“</strong> и посочете желания файл!
          </p>
        </div>
      </div>

      {/* Video Modal Preview */}
      {previewVideo && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl border border-slate-800 flex flex-col">
            <div className="p-3 bg-slate-950 flex items-center justify-between text-white border-b border-slate-800">
              <span className="text-xs font-mono font-bold text-[#C5A46E] truncate max-w-md">{previewVideo}</span>
              <button 
                onClick={() => setPreviewVideo(null)} 
                className="p-1 text-slate-400 hover:text-white rounded cursor-pointer"
              >
                ✕
              </button>
            </div>
            <div className="aspect-video bg-black flex items-center justify-center">
              <video 
                src={previewVideo} 
                controls 
                autoPlay 
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Modal Preview */}
      {previewImage && (
        <div 
          onClick={() => setPreviewImage(null)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl">
            <img 
              src={previewImage} 
              alt="Preview" 
              className="w-full h-full object-contain max-h-[85vh] rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default MediaLibrary;
